import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Lock, ShieldCheck } from "lucide-react";
import logo from "../assets/spacem8-logo.png";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

export default function UpdatePassword({ isDark }) {
  const { user, loading } = useAuth();
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const prepareRecoverySession = async () => {
      if (!supabase) {
        if (isMounted) setIsReady(true);
        return;
      }

      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");

      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError && isMounted) {
          setError(sessionError.message || "This password reset link is no longer valid.");
        }
      }

      if (isMounted) {
        setIsReady(true);
      }
    };

    prepareRecoverySession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Use at least 8 characters for the new password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    if (!supabase) {
      setError("SpaceM8 is missing its Supabase configuration.");
      return;
    }

    setIsSubmitting(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password,
    });

    setIsSubmitting(false);

    if (updateError) {
      setError(updateError.message || "Unable to update your password.");
      return;
    }

    toast.success("Password updated", "Your SpaceM8 password has been changed successfully.");
    setPassword("");
    setConfirmPassword("");
    setIsComplete(true);
  };

  if (!isReady || loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
        Preparing password reset...
      </main>
    );
  }

  if (user && !error) {
    return (
      <main
        className={`min-h-screen px-4 py-8 ${
          isDark
            ? "bg-slate-950 text-white"
            : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
        }`}
      >
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center justify-center">
          <div
            className={`grid w-full overflow-hidden rounded-3xl border shadow-sm lg:grid-cols-[1fr_420px] ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <section className={`p-8 md:p-12 ${isDark ? "bg-slate-900" : "bg-sky-50"}`}>
              <img src={logo} alt="SpaceM8" className="h-20 w-auto object-contain" />
              <div className="mt-10 max-w-xl">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                  Password Security
                </p>
                <h1 className={`mt-3 text-4xl font-semibold ${isDark ? "text-white" : "text-slate-950"}`}>
                  Set a new password for your account.
                </h1>
                <p className={`mt-4 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Finish the recovery flow by choosing a strong password you have not used before.
                </p>
              </div>

              <div className="mt-10 grid gap-4 md:grid-cols-3">
                {["Recovery Session", "Password Update", "Secure Access"].map((item) => (
                  <div
                    key={item}
                    className={`rounded-2xl border p-4 ${
                      isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"
                    }`}
                  >
                    <ShieldCheck className="h-5 w-5 text-sky-700" />
                    <p className={`mt-3 text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="p-8 md:p-10">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                  Update Password
                </p>
                <h2 className={`mt-2 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-950"}`}>
                  {isComplete ? "Password updated" : "Choose a new password"}
                </h2>
              </div>

              {isComplete ? (
                <div className="mt-8 space-y-5">
                  <div
                    className={`rounded-2xl border px-4 py-4 text-sm ${
                      isDark
                        ? "border-emerald-800 bg-emerald-950 text-emerald-300"
                        : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    Your password has been updated successfully. You can now continue into SpaceM8.
                  </div>

                  <Link
                    to="/"
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600"
                  >
                    Continue to dashboard
                  </Link>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className={`text-sm font-medium ${
                        isDark ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-800"
                      }`}
                    >
                      Back to login
                    </Link>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <label className="block">
                  <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                    New password
                  </span>
                  <span className="relative mt-2 block">
                    <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      minLength={8}
                      className={`h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                        isDark
                          ? "border-slate-700 bg-slate-950 text-white"
                          : "border-slate-200 bg-white text-slate-900"
                      }`}
                    />
                  </span>
                </label>

                <label className="block">
                  <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                    Confirm password
                  </span>
                  <span className="relative mt-2 block">
                    <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      required
                      minLength={8}
                      className={`h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                        isDark
                          ? "border-slate-700 bg-slate-950 text-white"
                          : "border-slate-200 bg-white text-slate-900"
                      }`}
                    />
                  </span>
                </label>

                {error ? (
                  <div
                    className={`rounded-2xl border px-4 py-3 text-sm ${
                      isDark
                        ? "border-rose-800 bg-rose-950 text-rose-300"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                    }`}
                  >
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`h-12 w-full rounded-2xl px-5 font-medium text-white transition ${
                    isSubmitting ? "cursor-not-allowed bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
                  }`}
                >
                  {isSubmitting ? "Updating..." : "Update password"}
                </button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className={`text-sm font-medium ${
                      isDark ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-800"
                    }`}
                  >
                    Back to login
                  </Link>
                </div>
              </form>
              )}
            </section>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      className={`min-h-screen px-4 py-8 ${
        isDark
          ? "bg-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl items-center justify-center">
        <section
          className={`w-full rounded-[2rem] border p-8 shadow-xl ${
            isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
          }`}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
            Password Recovery
          </p>
          <h1 className={`mt-3 text-3xl font-semibold ${isDark ? "text-white" : "text-slate-950"}`}>
            This reset link is not active yet.
          </h1>
          <p className={`mt-4 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            Open the newest password recovery email again, or request another reset link from the login
            page.
          </p>

          {error ? (
            <div
              className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${
                isDark
                  ? "border-rose-800 bg-rose-950 text-rose-300"
                  : "border-rose-200 bg-rose-50 text-rose-700"
              }`}
            >
              {error}
            </div>
          ) : null}

          <div className="mt-6">
            <Link
              to="/login"
              className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600"
            >
              Back to login
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
